/* ------------------------------------------------------------------------
Copyright (c) 2016, VoiceVibes, Inc

VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
All contents of this file are proprietary and confidential. No part of
any of the contents of this file may be disclosed in any manner to
a third party without the prior written consent of VoiceVibes, Inc.
Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    class="col-lg-10"
  >
    <form @submit.prevent="save">
      <h2>
        Assignment Criteria
      </h2>
      <div v-if="criteriaLocked">
        Assignment criteria cannot be modified for assignments that have
        already been assigned to users.<br>
        If needed, you may archive this assignment and create a new one.<br><br>
      </div>
      <div v-else>
        For an assignment to be marked complete, users must:
      </div>
      <div
        class="form-group"
        style="margin-top: 10px;"
      >
        <input
          id="recordAtLeast"
          v-model="assignment.criteria.minRecordings"
          type="checkbox"
          value="1"
          :disabled="criteriaLocked"
          @change="validateMinRecordingsCount"
        >
        <label
          for="recordAtLeast"
          class="control-label lower"
        >
          Record at least
        </label>
        <input
          id="recordAtLeastCount"
          v-model="assignment.criteria.minRecordingsCount"
          type="number"
          min="0"
          :disabled="criteriaLocked"
          style="width: 50px"
        >
        <span style="font-size: 11px">
          recordings
        </span>
        <br>

        <input
          id="recordAtLeastRating"
          v-model="assignment.criteria.minRating"
          type="checkbox"
          value="1"
          :disabled="criteriaLocked"
          @change="validateMinRatingNum"
        >
        <label
          for="recordAtLeastRating"
          class="control-label lower"
        >
          Receive a coach overall rating of at least
          <input
            id="recordAtLeastRatingNum"
            v-model="assignment.criteria.minRatingNum"
            type="number"
            min="1"
            max="10"
            :disabled="criteriaLocked"
            style="width: 50px"
          >
        </label>
        <br>

        <input
          id="recordAtLeastScore"
          v-model="assignment.criteria.minScore"
          type="checkbox"
          value="1"
          :disabled="criteriaLocked"
          @change="validateMinScoreNum"
        >
        <label
          for="recordAtLeastScore"
          class="control-label lower"
        >
          Receive an automated Vocal Delivery score of at least
          <input
            id="recordAtLeastScoreNum"
            v-model="assignment.criteria.minScoreNum"
            type="number"
            min="1"
            max="10"
            :disabled="criteriaLocked"
            style="width: 50px"
          >
        </label>
        <br>

        <input
          id="managerReview"
          v-model="assignment.criteria.managerReview"
          type="checkbox"
          :disabled="criteriaLocked"
          value="1"
        >
        <label
          for="managerReview"
          class="control-label lower"
        >
          Flag one recording for manager review
        </label>
        <br>

        <input
          id="shareRecording"
          v-model="assignment.criteria.shareRecording"
          type="checkbox"
          :disabled="criteriaLocked"
          value="1"
        >
        <label
          for="shareRecording"
          class="control-label lower"
        >
          Share at least 1 recording with the group
        </label>
        <br>

        <input
          id="selfCommentary"
          v-model="assignment.criteria.selfCommentary"
          type="checkbox"
          :disabled="criteriaLocked"
          value="1"
        >
        <label
          for="selfCommentary"
          class="control-label lower"
        >
          Provide a self commentary (via a feedback comment they leave on their own recording)
        </label>
        <br>

        <h3>
          Optional Customization
        </h3>
        <input
          id="wordspotting"
          v-model="assignment.criteria.wordspotting"
          type="checkbox"
          value="1"
        >
        <label
          for="wordspotting"
          class="control-label lower"
        >
          Add custom wordspotting to analysis results
        </label>
        <br>
        <input
          id="scorecard"
          v-model="assignment.criteria.scorecard"
          :disabled="scorecardDisabled"
          type="checkbox"
          value="1"
        >
        <label
          for="scorecard"
          class="control-label lower"
        >
          Customize Scorecard
        </label>
        <br>
        <br>
        <div
          v-if="showAlgorithm"
          class="form-group"
        >
          <label
            for="orgAlgorithmConfig"
            class="control-label"
          >
            Number of Speakers per Recording:
          </label>
          <span
            v-if="oldAlgoDetected"
          >
            {{ oldAlgoName }}
          </span>
          <select
            v-else
            id="orgAlgorithmConfig"
            v-model="assignment.criteria.algorithm"
            :disabled="algorithmDisabled"
            class="form-control"
            style="width: 300px;"
            required
          >
            <option
              :value="getAlgo('default')"
              selected
            >
              Enforce Single Speaker Recordings
            </option>
            <option
              :value="getAlgo('conversational')"
              selected
            >
              Enforce Two Speaker Recordings
            </option>
          </select>
          <br>
        </div>
      </div>

      <button
        type="button"
        class="btn clear-btn"
        @click="$emit('rewindAssignmentEditor')"
      >
        Cancel / Previous
      </button>
      <loading-button
        style="float:right"
        :loading="saving"
      >
        Save and Continue
      </loading-button>
    </form>
  </div>
</template>

<script>
export default {
  props: [ 'assignment' ],
  data() {
    return {
      saving: false,
      assignmentAlgorithm: null,
    };
  },
  computed: {
    scorecardDisabled: {
      cache: false,
      get() {
        return !!(this.assignment.scorecard && this.assignment.scorecard.id);
      }
    },
    defaultAlgoConfig: {
      cache: false,
      get() {
        return this.$store.getters.org.algorithmConfig.default_algorithm;
      }
    },
    oldAlgoDetected: {
      cache: false,
      get() {
        const algo = this.assignment.criteria.algorithm;
        return (algo && !this._.find(
          this.$store.getters.org.algorithmConfig.algorithms,
          {name: algo}
        ));
      }
    },
    oldAlgoName: {
      cache: false,
      get() {
        return this.assignment.criteria.algorithm.indexOf('conversational') !== -1 ?
          'Multi-Speaker' : 'Single Speaker';
      }
    },
    showAlgorithm: {
      cache: false,
      get() {
        const algorithms = this.$store.getters.org.algorithmConfig.algorithms;
        let hasSingle, hasMulti;
        algorithms.forEach((algo) => {
          if (algo.name.indexOf('default') !== -1) hasSingle = true;
          if (algo.name.indexOf('conversational') !== -1) hasMulti = true;
        });
        return !!(hasSingle === true && hasMulti === true);
      }
    },
    algorithmDisabled: {
      cache: false,
      get() {
        return !this.assignment.isTemplate; // Only templates permit algorithm selection
      }
    },
    coreScores() {
      return this.$store.getters.appConfig.coreScores;
    },
    criteriaLocked: {
      cache: false,
      get() {
        return !this.assignment.isTemplate;
      }
    }
  },
  methods: {
    getAlgo(identifier) {
      const algorithms = this.$store.getters.org.algorithmConfig.algorithms;
      let algorithm;
      algorithms.forEach((algo) => {
        if (algo.name.indexOf(identifier) !== -1) {
          // Choose the correct algorithm, v3-default, v4-conversational, etc.
          algorithm = algo.name;
        }
      });
      return algorithm;
    },
    validateMinRecordingsCount() {
      if (this.assignment.criteria.minRecordings &&
        !parseInt(this.assignment.criteria.minRecordingsCount, 10)) {
        this.assignment.criteria.minRecordingsCount = 1;
      }
    },
    validateMinScoreNum() {
      if (!this.assignment.criteria.minScore) {
        delete this.assignment.criteria.minScoreNum;
      }
      if (this.assignment.criteria.minScore &&
        !parseInt(this.assignment.criteria.minScoreNum, 10)) {
        this.assignment.criteria.minScoreNum = 4;
      }
    },
    validateMinRatingNum() {
      if (!this.assignment.criteria.minRating) {
        delete this.assignment.criteria.minRatingNum;
      }
      if (this.assignment.criteria.minRating &&
        !parseInt(this.assignment.criteria.minRatingNum, 10)) {
        this.assignment.criteria.minRatingNum = 4;
      }
    },
    save() {
      this.validateMinRecordingsCount();
      this.validateMinScoreNum();

      this.saving = true;

      if (this.assignment.assignmentId) {
        // Do not mutate state
        const assignment = this._.cloneDeep(this.assignment);
        if (!assignment.metadata) assignment.metadata = {};

        // Ensure criteria has an algorithm
        if (!assignment.criteria.algorithm) {
          assignment.criteria.algorithm = this.defaultAlgoConfig;
        }

        this.$store.dispatch('updateAssignment', {assignment})
          .then(() => {
            // Fire and forget a request to reset wordspotting
            if (!this.assignment.criteria.wordspotting) {
              this.assignment.wordspotting = ['resetWordspotting'];
              this.$store.dispatch('updateAssignment', {assignment: this.assignment, updateWordspotting: true});
            }

            // Advance to the next form
            this.$emit('advanceAssignmentEditor');
          })
          .catch(() => {
            this.saving = false;
          });
      }
    },
  }
};
</script>
